import { LifeEvents } from 'modules/af_tool/api/actions/types';
import { format } from 'utils/date';
import { createApplicantFormValues, createProductLabel } from '.';
import {
  InitialData,
  Resources,
  FetchedResources,
  TFormValues,
  Payload,
  FetchedContext,
  Context,
} from '../CalculationId.types';
import { ApplicantKey, FIELDS } from '../partials/Form/Form.types';
import { CurrentIncome, FutureIncome } from './incomes';
import { currentPensions, futurePensions } from './pensions';
import * as liabilitiesUtil from './liabilities';
import { liabilityTypeDirectorFactory } from './liabilities';
import { PRODUCT_STATUS } from './types';

export const mapInitialData = (initialData: InitialData): TFormValues => {
  const numberOfApplicants = [initialData.loanData.applicant1, initialData.loanData.applicant2].filter((a) => a).length;
  const mapApplicant = (
    applicantInitialData: NonNullable<InitialData['loanData']['applicant1']>,
  ): TFormValues['applicant1'] => {
    return {
      [FIELDS.APPLICANT_DATE_OF_BIRTH]: applicantInitialData.dateOfBirth,
      [FIELDS.APPLICANT_GENDER]: applicantInitialData.gender,
      [FIELDS.APPLICANT_MARITAL_STATUS]: applicantInitialData.maritalStatus,
      [FIELDS.APPLICANT_LIFE_STAGE]: applicantInitialData.lifeStage,
      [FIELDS.APPLICANT_RETIREMENT_AGE]: applicantInitialData.retirementAge,
      [FIELDS.FUTURE_INCOME_PREFIX]: FutureIncome.mapApi(applicantInitialData.futureIncome),
      [FIELDS.CURRENT_INCOME_PREFIX]: CurrentIncome.mapApi(applicantInitialData.currentIncome),
      [FIELDS.LIFE_EVENTS_PREFIX]: {
        [FIELDS.INVESTMENT_PORTFOLIO_VALUE]: Number(applicantInitialData.investmentPortfolioValue),
        [FIELDS.VALUE_OF_ASSET_AT_RETIREMENT]: Number(applicantInitialData.valueOfAssetsAtRetirement),
        [FIELDS.MORTGAGE_PROTECTION_MONTHLY_PAYMENT]: Number(applicantInitialData.mortgageProtectionMonthlyPayments),
        [FIELDS.LIFE_INSURANCE_LUMP_SUM]: Number(applicantInitialData.lifeInsuranceLumpSum),
        [FIELDS.LIFE_INSURANCE_PREMIUM]: Number(applicantInitialData.lifeInsurancePremium),
      },
      [FIELDS.CURRENT_PENSION_PREFIX]: currentPensions.mapApi(applicantInitialData.currentPensions),
      [FIELDS.FUTURE_PENSION_PREFIX]: futurePensions.mapApi(applicantInitialData.futurePensions),
      [FIELDS.LIABILITY_PREFIX]: applicantInitialData.liabilities.length
        ? applicantInitialData.liabilities.map(liabilitiesUtil.mapLiabilityResponseItem)
        : [],
    };
  };

  return {
    [FIELDS.NUMBER_OF_APPLICANTS]: numberOfApplicants,
    [FIELDS.APPLICANT_1]:
      numberOfApplicants > 0 && initialData.loanData.applicant1
        ? mapApplicant(initialData.loanData.applicant1)
        : createApplicantFormValues(),
    [FIELDS.APPLICANT_2]:
      numberOfApplicants > 1 && initialData.loanData.applicant2
        ? mapApplicant(initialData.loanData.applicant2)
        : createApplicantFormValues(),
    [FIELDS.LOAN_DETAILS_PREFIX]: {
      [FIELDS.LOAN_REQUESTED]: initialData.loanData.loanRequested,
      [FIELDS.MORTGAGE_PRODUCT]: initialData.loanData.productCode,
      [FIELDS.PROPERTY_VALUE]: initialData.loanData.propertyValue,
      [FIELDS.IS_PRIMARY_HOME]: typeof initialData.loanData.isPrimaryHome === 'boolean'
        ? initialData.loanData.isPrimaryHome ? 'true' : 'false'
        : null,
      [FIELDS.POST_CODE]: initialData.loanData.postCode,
      [FIELDS.MONTHLY_LIVING_COSTS]: initialData.loanData.monthlyLivingCosts,
      [FIELDS.ANNUAL_COUNCIL_TAX]: initialData.loanData.annualCouncilTax,
      [FIELDS.ANNUAL_GROUND_RENT_SERVICE_CHARGE]: initialData.loanData.annualGroundRent,
      [FIELDS.TERM_LENGTH]: initialData.loanData.termLength,
      [FIELDS.PRODUCT_REPAYMENT_TYPE]: initialData.loanData.productRepaymentType,
    },
  };
};

export const mapContext = (fetchedContext: FetchedContext): Context => {
  return {
    lastUpdated: format(fetchedContext.lastUpdated).dateTime('hh:mma, DD/MM/YYYY'),
    name: fetchedContext.name,
    relatedCaseNumber: fetchedContext.relatedCaseNumber,
  };
};

export const mapFormValues = (formValues: TFormValues): Payload => {
  const mapApplicant = (applicantFormValues: NonNullable<TFormValues[ApplicantKey]>): Payload[ApplicantKey] => {
    const mapLifeEvents = (
      liveEventsFormValues: NonNullable<TFormValues[ApplicantKey]>[FIELDS.LIFE_EVENTS_PREFIX],
    ): LifeEvents => {
      return {
        investmentPortfolioValue: liveEventsFormValues[FIELDS.INVESTMENT_PORTFOLIO_VALUE],
        valueOfAssetsAtRetirement: liveEventsFormValues[FIELDS.VALUE_OF_ASSET_AT_RETIREMENT],
        mortgageProtectionMonthlyPayments: liveEventsFormValues[FIELDS.MORTGAGE_PROTECTION_MONTHLY_PAYMENT],
        lifeInsuranceLumpSum: liveEventsFormValues[FIELDS.LIFE_INSURANCE_LUMP_SUM],
        lifeInsurancePremium: liveEventsFormValues[FIELDS.LIFE_INSURANCE_PREMIUM],
      };
    };

    return {
      currentIncome: CurrentIncome.mapForm(applicantFormValues[FIELDS.CURRENT_INCOME_PREFIX]),
      futureIncome: FutureIncome.mapForm(applicantFormValues[FIELDS.FUTURE_INCOME_PREFIX]),
      dateOfBirth: format(applicantFormValues[FIELDS.APPLICANT_DATE_OF_BIRTH] as string).fromViewToRequest(),
      gender: applicantFormValues[FIELDS.APPLICANT_GENDER] as string,
      maritalStatus: applicantFormValues[FIELDS.APPLICANT_MARITAL_STATUS] as string,
      lifeStage: applicantFormValues[FIELDS.APPLICANT_LIFE_STAGE] as string,
      retirementAge:
        applicantFormValues[FIELDS.APPLICANT_LIFE_STAGE] === 'NOT_RETIRED'
          ? Number(applicantFormValues[FIELDS.APPLICANT_RETIREMENT_AGE])
          : null,
      ...mapLifeEvents(applicantFormValues[FIELDS.LIFE_EVENTS_PREFIX]),
      currentPensions: currentPensions.mapForm(applicantFormValues[FIELDS.CURRENT_PENSION_PREFIX]),
      futurePensions: futurePensions.mapForm(applicantFormValues[FIELDS.FUTURE_PENSION_PREFIX]),
      liabilities: applicantFormValues[FIELDS.LIABILITY_PREFIX].map((liability) => {
        const liablityTypeDirector = liabilityTypeDirectorFactory(liability.type);
        liablityTypeDirector.prepareData(liability);
        return liablityTypeDirector.getData();
      }),
    };
  };

  const productCode = formValues[FIELDS.LOAN_DETAILS_PREFIX][FIELDS.MORTGAGE_PRODUCT];

  return {
    loanRequested: formValues[FIELDS.LOAN_DETAILS_PREFIX][FIELDS.LOAN_REQUESTED] as number,
    postCode: (formValues[FIELDS.LOAN_DETAILS_PREFIX][FIELDS.POST_CODE] || '').toString(),
    monthlyLivingCosts: Number(formValues[FIELDS.LOAN_DETAILS_PREFIX][FIELDS.MONTHLY_LIVING_COSTS]),
    annualCouncilTax: Number(formValues[FIELDS.LOAN_DETAILS_PREFIX][FIELDS.ANNUAL_COUNCIL_TAX]),
    annualGroundRent: Number(formValues[FIELDS.LOAN_DETAILS_PREFIX][FIELDS.ANNUAL_GROUND_RENT_SERVICE_CHARGE]),
    productCode: productCode ? productCode : null,
    propertyValue: Number(formValues[FIELDS.LOAN_DETAILS_PREFIX][FIELDS.PROPERTY_VALUE]),
    isPrimaryHome: formValues[FIELDS.LOAN_DETAILS_PREFIX][FIELDS.IS_PRIMARY_HOME]
      ? formValues[FIELDS.LOAN_DETAILS_PREFIX][FIELDS.IS_PRIMARY_HOME] === 'true'
      : null,
    applicant1: mapApplicant(formValues[FIELDS.APPLICANT_1]) as Payload['applicant1'],
    applicant2:
      formValues.numberOfApplicants === 2
        ? (mapApplicant(
            formValues[FIELDS.APPLICANT_2] as NonNullable<TFormValues['applicant2']>,
          ) as Payload['applicant2'])
        : null,
    termLength: formValues.loanDetails[FIELDS.TERM_LENGTH],
    productRepaymentType: formValues.loanDetails[FIELDS.PRODUCT_REPAYMENT_TYPE],
  };
};

export const mapResources = (resources: FetchedResources): Resources => {
  const statusesOrder = {
    [PRODUCT_STATUS.COMMISSIONED]: 0,
    [PRODUCT_STATUS.DECOMMISSIONED]: 1,
  };

  const productsOrderedByCodeAndStatus = resources.products
    .sort((a, b) => a.code.localeCompare(b.code))
    .sort((a, b) => statusesOrder[a.status as PRODUCT_STATUS] - statusesOrder[b.status as PRODUCT_STATUS]);

  return {
    businessType: resources.businessType,
    gender: resources.gender,
    lifeStage: resources.lifeStage,
    products: productsOrderedByCodeAndStatus.map((product) => {
      return {
        option: createProductLabel(product),
        value: product.code,
        params: {
          productType: product.type,
          code: product.code,
          availableRepaymentTypes: product.availableRepaymentTypes,
        },
      };
    }),
    useOfFunds: resources.useOfFunds,
    liabilities: resources.liabilities,
    maritalStatus: resources.maritalStatus,
    termLengths: resources.termLengths.map((termLength) => ({ ...termLength, option: `${termLength.option} years` })),
  };
};
